import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Plus,
  Trash2,
  FilePlus2,
  Save,
  Building2,
  User as UserIcon,
  Sparkles,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const todayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const emptyItem = () => ({
  description: "",
  hsn: "",
  quantity: 1,
  unit: "Pcs",
  unit_price: 0,
  gst_rate: 18,
});

export default function ManualInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = location.state?.preset; // optional pre-fill from "Duplicate" flow
  const [seller, setSeller] = useState(null);
  const [saving, setSaving] = useState(false);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayDDMMYYYY());

  const [buyer, setBuyer] = useState(
    preset?.buyer
      ? {
          name: preset.buyer.name || "",
          address: preset.buyer.address || "",
          city: preset.buyer.city || "",
          state: preset.buyer.state || "",
          state_code: preset.buyer.state_code || "",
          gstin: preset.buyer.gstin || "",
          pan: preset.buyer.pan || "",
        }
      : {
          name: "",
          address: "",
          city: "",
          state: "",
          state_code: "",
          gstin: "",
          pan: "",
        },
  );

  const [lineItems, setLineItems] = useState(
    preset?.line_items && preset.line_items.length
      ? preset.line_items.map((it) => ({
          description: it.description || "",
          hsn: it.hsn || "",
          quantity: Number(it.quantity) || 1,
          unit: it.unit || "Pcs",
          unit_price: Number(it.unit_price) || 0,
          gst_rate: Number(it.gst_rate) || 18,
        }))
      : [emptyItem()],
  );
  const [notes, setNotes] = useState(preset?.notes || "");

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sugLoading, setSugLoading] = useState(false);
  const [sugIdx, setSugIdx] = useState(-1);
  const sugDebounceRef = useRef(null);
  const buyerWrapRef = useRef(null);

  // GSTIN auto-fetch state
  const [gstinLookup, setGstinLookup] = useState({
    loading: false,
    success: false,
    lastGstin: "",
    error: "",
  });
  const gstinDebounceRef = useRef(null);

  const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  useEffect(() => {
    api
      .get("/settings/seller")
      .then(({ data }) => setSeller(data))
      .catch(() => toast.error("Failed to load Seller Profile"));
  }, []);

  // Show a toast once if we arrived via Duplicate
  useEffect(() => {
    if (preset?.sourceInvoiceNumber) {
      toast.success(`Pre-filled from ${preset.sourceInvoiceNumber} — edit and generate`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (buyerWrapRef.current && !buyerWrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = (q) => {
    if (sugDebounceRef.current) clearTimeout(sugDebounceRef.current);
    sugDebounceRef.current = setTimeout(async () => {
      try {
        setSugLoading(true);
        const { data } = await api.get(`/buyers${q ? `?q=${encodeURIComponent(q)}` : ""}`);
        setSuggestions(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setSugLoading(false);
      }
    }, 180);
  };

  const pickSuggestion = (b) => {
    setBuyer({
      name: b.name || "",
      address: b.address || "",
      city: b.city || "",
      state: b.state || "",
      state_code: b.state_code || "",
      gstin: b.gstin || "",
      pan: b.pan || "",
    });
    setShowSuggestions(false);
    setSugIdx(-1);
    toast.success(`Loaded ${b.name}`);
  };

  // Auto-derive buyer state_code from GSTIN if user leaves it empty + trigger auto-fetch
  const updBuyer = (k, v) => {
    setBuyer((p) => {
      const next = { ...p, [k]: v };
      if (k === "gstin" && v && !p.state_code && v.length >= 2) {
        next.state_code = v.slice(0, 2);
      }
      return next;
    });
    if (k === "gstin") {
      scheduleGstinLookup(v);
    }
  };

  const scheduleGstinLookup = (raw) => {
    const g = (raw || "").trim().toUpperCase();
    if (gstinDebounceRef.current) clearTimeout(gstinDebounceRef.current);
    if (!GSTIN_REGEX.test(g)) {
      // Not a complete GSTIN yet — clear any stale status (but keep user's fields)
      setGstinLookup((s) => ({ ...s, loading: false, success: false, error: "" }));
      return;
    }
    if (g === gstinLookup.lastGstin && gstinLookup.success) {
      return; // already fetched this one
    }
    setGstinLookup({ loading: true, success: false, lastGstin: g, error: "" });
    gstinDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/gstin/${g}`);
        if (data?.found) {
          setBuyer((p) => ({
            ...p,
            // Only overwrite blank fields OR fields that look like placeholders.
            // Always fill name/address because that's the point of the lookup.
            name: p.name && p.name.trim().toLowerCase() !== data.name.trim().toLowerCase() ? p.name : data.name,
            address: p.address?.trim() ? p.address : data.address,
            city: p.city?.trim() ? p.city : data.city,
            state: p.state?.trim() ? p.state : data.state,
            state_code: data.state_code || p.state_code,
            gstin: data.gstin || g,
            pan: p.pan?.trim() ? p.pan : data.pan,
          }));
          setGstinLookup({ loading: false, success: true, lastGstin: g, error: "" });
          toast.success(`Fetched: ${data.name}`);
        } else {
          setGstinLookup({
            loading: false,
            success: false,
            lastGstin: g,
            error: data?.error || "GSTIN not found",
          });
          toast.info(data?.error || "GSTIN not found — fill the fields manually");
        }
      } catch (e) {
        console.error(e);
        setGstinLookup({
          loading: false,
          success: false,
          lastGstin: g,
          error: e?.response?.data?.detail || "Lookup failed",
        });
        toast.info("Could not auto-fetch — please fill the fields manually");
      }
    }, 450);
  };

  const updItem = (idx, k, v) =>
    setLineItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));

  const addItem = () => {
    const lastGst = lineItems.length ? Number(lineItems[lineItems.length - 1].gst_rate) || 18 : 18;
    setLineItems((p) => [...p, { ...emptyItem(), gst_rate: lastGst }]);
  };
  const removeItem = (idx) => setLineItems((p) => p.filter((_, i) => i !== idx));

  const sellerStateCode = (seller?.state_code || seller?.gstin?.slice(0, 2) || "").trim();
  const buyerStateCode = (buyer.state_code || buyer.gstin?.slice(0, 2) || "").trim();

  const intraState = useMemo(() => {
    if (!sellerStateCode || !buyerStateCode) return null; // unknown yet
    return sellerStateCode === buyerStateCode;
  }, [sellerStateCode, buyerStateCode]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    for (const it of lineItems) {
      const q = Number(it.quantity) || 0;
      const r = Number(it.unit_price) || 0;
      const g = Number(it.gst_rate) || 0;
      subtotal += q * r;
      tax += (q * r * g) / 100;
    }
    const grand = subtotal + tax;
    return {
      subtotal,
      tax,
      cgst: intraState ? tax / 2 : 0,
      sgst: intraState ? tax / 2 : 0,
      igst: intraState ? 0 : tax,
      grand,
    };
  }, [lineItems, intraState]);

  const canSubmit =
    !!buyer.name.trim() &&
    lineItems.length > 0 &&
    lineItems.every(
      (it) =>
        it.description.trim() &&
        Number(it.quantity) > 0 &&
        Number(it.unit_price) >= 0,
    );

  const submit = async () => {
    if (!seller?.name) {
      toast.error("Set up your Seller Profile first");
      navigate("/settings");
      return;
    }
    if (!buyer.name.trim()) {
      toast.error("Buyer name is required");
      return;
    }
    if (!lineItems.length || lineItems.some((it) => !it.description.trim())) {
      toast.error("Add at least one line item with a description");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        po_number: "",
        po_date: "",
        invoice_number: invoiceNumber.trim() || null,
        invoice_date: invoiceDate.trim() || todayDDMMYYYY(),
        buyer: { ...buyer },
        ship_to: { ...buyer }, // default ship_to = bill_to
        line_items: lineItems.map((it) => ({
          ...it,
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          gst_rate: Number(it.gst_rate) || 0,
        })),
        place_of_supply: buyer.state || "",
        notes,
        tax_type: "auto", // backend decides via state codes
      };
      const { data: inv } = await api.post("/invoice/generate", payload);
      toast.success(`Invoice ${inv.invoice_number} created`);
      navigate(`/invoice/${inv.id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  const sellerAddress = seller
    ? [seller.address_line1, seller.address_line2, [seller.city, seller.pin].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="manual-back">
          <ChevronLeft size={14} className="mr-1" /> Back
        </Button>
        <Badge variant="outline" className="mono text-[10px]">
          <Sparkles size={10} className="mr-1" />
          Manual entry
        </Badge>
      </div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            Dashboard · Manual Invoice
          </div>
          <h1 className="text-4xl font-bold tracking-tight" data-testid="manual-heading">
            Create Manual Invoice
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Enter buyer and line items yourself — seller details come from your
            Profile. CGST+SGST or IGST is calculated automatically from the
            state codes.
          </p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={submit}
          disabled={saving || !canSubmit}
          data-testid="manual-create-btn"
        >
          <Save size={14} className="mr-1" />
          {saving ? "Creating…" : "Create & Preview Invoice"}
        </Button>
      </div>

      {/* Seller readout */}
      <Card className="p-4 mb-5 bg-zinc-50 border-zinc-200" data-testid="manual-seller-card">
        <div className="flex items-start gap-3">
          <Building2 size={16} className="text-blue-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">
              Seller (auto-filled from profile)
            </div>
            {seller?.name ? (
              <div className="mt-1 text-sm" data-testid="manual-seller-name">
                <b>{seller.name}</b>
                <span className="text-zinc-500 ml-2 mono text-xs">
                  GSTIN {seller.gstin || "—"} · {seller.state} ({sellerStateCode || "—"})
                </span>
                <div className="text-xs text-zinc-600 mt-1 truncate">{sellerAddress}</div>
              </div>
            ) : (
              <div className="mt-1 text-sm text-amber-700">
                Seller Profile is empty.{" "}
                <button
                  type="button"
                  className="underline"
                  onClick={() => navigate("/settings")}
                >
                  Set it up →
                </button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Invoice meta */}
      <Card className="p-5 mb-5">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">
          Invoice Details
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              Invoice Number
            </Label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Leave blank for auto-numbering"
              className="h-9 mt-1 text-sm mono"
              data-testid="manual-invoice-number"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              Invoice Date (DD-MM-YYYY)
            </Label>
            <Input
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              placeholder="DD-MM-YYYY"
              className="h-9 mt-1 text-sm mono"
              data-testid="manual-invoice-date"
            />
          </div>
        </div>
      </Card>

      {/* Buyer */}
      <Card className="p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <UserIcon size={14} className="text-violet-600" />
          <div className="text-xs uppercase tracking-wider text-zinc-500">
            Buyer / Customer
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2" ref={buyerWrapRef}>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              Company Name <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                value={buyer.name}
                onChange={(e) => {
                  const v = e.target.value;
                  updBuyer("name", v);
                  fetchSuggestions(v);
                  setShowSuggestions(true);
                  setSugIdx(-1);
                }}
                onFocus={() => {
                  fetchSuggestions(buyer.name);
                  setShowSuggestions(true);
                }}
                onKeyDown={(e) => {
                  if (!showSuggestions || !suggestions.length) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSugIdx((i) => Math.min(suggestions.length - 1, i + 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSugIdx((i) => Math.max(0, i - 1));
                  } else if (e.key === "Enter" && sugIdx >= 0) {
                    e.preventDefault();
                    pickSuggestion(suggestions[sugIdx]);
                  } else if (e.key === "Escape") {
                    setShowSuggestions(false);
                  }
                }}
                placeholder="Start typing to see saved buyers…"
                autoComplete="off"
                className="h-9 mt-1 text-sm"
                data-testid="manual-buyer-name"
              />
              {showSuggestions && (suggestions.length > 0 || sugLoading) && (
                <div
                  className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-md border border-zinc-200 bg-white shadow-md"
                  data-testid="buyer-suggestions"
                >
                  {sugLoading && suggestions.length === 0 && (
                    <div className="px-3 py-2 text-xs text-zinc-400">Searching…</div>
                  )}
                  {suggestions.map((s, i) => (
                    <button
                      type="button"
                      key={(s.gstin || "") + s.name}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickSuggestion(s);
                      }}
                      onMouseEnter={() => setSugIdx(i)}
                      className={`w-full text-left px-3 py-2 text-sm border-b border-zinc-100 last:border-b-0 ${
                        i === sugIdx ? "bg-blue-50" : "bg-white hover:bg-zinc-50"
                      }`}
                      data-testid={`buyer-suggestion-${i}`}
                    >
                      <div className="font-medium truncate">{s.name}</div>
                      <div className="text-[11px] text-zinc-500 mono truncate">
                        {s.gstin ? `${s.gstin} · ` : ""}
                        {s.state || "—"}
                        {s.state_code ? ` (${s.state_code})` : ""}
                        <span className="ml-2 text-zinc-400">
                          · used {s.invoice_count || 1}x
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              Address
            </Label>
            <Textarea
              value={buyer.address}
              onChange={(e) => updBuyer("address", e.target.value)}
              placeholder="Street, Area, Landmark, PIN"
              className="mt-1 text-sm min-h-[60px]"
              data-testid="manual-buyer-address"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">City</Label>
            <Input
              value={buyer.city}
              onChange={(e) => updBuyer("city", e.target.value)}
              className="h-9 mt-1 text-sm"
              data-testid="manual-buyer-city"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">State</Label>
            <Input
              value={buyer.state}
              onChange={(e) => updBuyer("state", e.target.value)}
              placeholder="e.g. Karnataka"
              className="h-9 mt-1 text-sm"
              data-testid="manual-buyer-state"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">GSTIN</Label>
            <div className="relative">
              <Input
                value={buyer.gstin}
                onChange={(e) => updBuyer("gstin", e.target.value.toUpperCase())}
                placeholder="15-char GSTIN (auto-fills from GST portal)"
                className="h-9 mt-1 text-sm mono pr-8"
                maxLength={15}
                data-testid="manual-buyer-gstin"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 pointer-events-none">
                {gstinLookup.loading && (
                  <Loader2
                    size={14}
                    className="text-blue-600 animate-spin"
                    data-testid="gstin-lookup-spinner"
                  />
                )}
                {!gstinLookup.loading && gstinLookup.success && (
                  <CheckCircle2
                    size={14}
                    className="text-emerald-600"
                    data-testid="gstin-lookup-success"
                  />
                )}
              </div>
            </div>
            {gstinLookup.error && !gstinLookup.loading && (
              <div className="text-[10px] text-amber-700 mt-1" data-testid="gstin-lookup-error">
                {gstinLookup.error} — fill the fields manually below.
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              State Code
            </Label>
            <Input
              value={buyer.state_code}
              onChange={(e) => updBuyer("state_code", e.target.value)}
              placeholder="Auto-filled from GSTIN"
              className="h-9 mt-1 text-sm mono"
              data-testid="manual-buyer-state-code"
            />
          </div>
        </div>
      </Card>

      {/* Line items */}
      <Card className="p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Line Items</div>
          <Button
            variant="outline"
            size="sm"
            onClick={addItem}
            className="h-7 text-[11px] border-dashed"
            data-testid="manual-add-row"
          >
            <Plus size={12} className="mr-1" /> Add Row
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border border-zinc-200">
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                <th className="p-2 w-8 text-left">#</th>
                <th className="p-2 text-left">Description</th>
                <th className="p-2 w-24 text-left">HSN</th>
                <th className="p-2 w-20 text-right">Qty</th>
                <th className="p-2 w-24 text-right">Rate</th>
                <th className="p-2 w-20 text-right">GST %</th>
                <th className="p-2 w-28 text-right">Amount</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((it, i) => {
                const q = Number(it.quantity) || 0;
                const r = Number(it.unit_price) || 0;
                const g = Number(it.gst_rate) || 0;
                const amt = q * r * (1 + g / 100);
                return (
                  <tr key={i} className="border-t border-zinc-100" data-testid={`manual-row-${i}`}>
                    <td className="p-1.5 text-center mono text-zinc-500">{i + 1}</td>
                    <td className="p-1.5">
                      <Textarea
                        value={it.description}
                        onChange={(e) => updItem(i, "description", e.target.value)}
                        className="text-xs px-2 py-1 min-h-[36px] leading-snug"
                        data-testid={`manual-desc-${i}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        value={it.hsn}
                        onChange={(e) => updItem(i, "hsn", e.target.value)}
                        className="h-8 text-xs mono px-2"
                        data-testid={`manual-hsn-${i}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number"
                        value={it.quantity}
                        onChange={(e) => updItem(i, "quantity", e.target.value)}
                        className="h-8 text-xs mono text-right px-2"
                        data-testid={`manual-qty-${i}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number"
                        step="0.01"
                        value={it.unit_price}
                        onChange={(e) => updItem(i, "unit_price", e.target.value)}
                        className="h-8 text-xs mono text-right px-2"
                        data-testid={`manual-rate-${i}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number"
                        value={it.gst_rate}
                        onChange={(e) => updItem(i, "gst_rate", e.target.value)}
                        className="h-8 text-xs mono text-right px-2"
                        data-testid={`manual-gst-${i}`}
                      />
                    </td>
                    <td className="p-1.5 mono text-right tabular font-semibold">
                      {amt.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="p-1 text-center">
                      <button
                        type="button"
                        className="text-zinc-400 hover:text-red-600 disabled:opacity-30"
                        onClick={() => removeItem(i)}
                        disabled={lineItems.length === 1}
                        title={
                          lineItems.length === 1
                            ? "At least one line item is required"
                            : "Remove row"
                        }
                        data-testid={`manual-remove-${i}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Totals */}
      <Card className="p-5 mb-5" data-testid="manual-totals-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Totals</div>
          <Badge
            variant="outline"
            className={`text-[10px] mono ${
              intraState === null
                ? "text-zinc-500"
                : intraState
                ? "text-emerald-700 border-emerald-300"
                : "text-indigo-700 border-indigo-300"
            }`}
            data-testid="manual-tax-badge"
          >
            {intraState === null
              ? "Enter state codes"
              : intraState
              ? `Intra-state (CGST + SGST)  —  seller ${sellerStateCode} = buyer ${buyerStateCode}`
              : `Inter-state (IGST)  —  seller ${sellerStateCode} ≠ buyer ${buyerStateCode}`}
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="text-xs text-zinc-500 leading-relaxed">
            Tax category is derived automatically by comparing seller state code
            ({sellerStateCode || "unset"}) with buyer state code (
            {buyerStateCode || "unset"}). Edit the GSTIN or State Code above to
            change it.
          </div>
          <div className="rounded-md border border-zinc-200 bg-white divide-y divide-zinc-100 text-xs mono tabular">
            <Row label="Subtotal (Taxable)" value={totals.subtotal} testid="manual-subtotal" />
            {intraState ? (
              <>
                <Row label="CGST" value={totals.cgst} testid="manual-cgst" />
                <Row label="SGST" value={totals.sgst} testid="manual-sgst" />
              </>
            ) : (
              <Row label="IGST" value={totals.igst} testid="manual-igst" />
            )}
            <Row label="Grand Total (INR)" value={totals.grand} bold testid="manual-grand-total" />
          </div>
        </div>
      </Card>

      {/* Notes */}
      <Card className="p-5 mb-5">
        <Label className="text-xs uppercase tracking-wider text-zinc-500">
          Notes (optional)
        </Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Terms, remarks, bank details, delivery info, etc."
          className="mt-2 text-sm min-h-[70px]"
          data-testid="manual-notes"
        />
      </Card>

      <div className="flex items-center justify-between sticky bottom-4 bg-white border border-zinc-200 shadow-sm rounded-md px-4 py-2.5">
        <div className="text-xs text-zinc-500">
          <FilePlus2 size={13} className="inline mr-1 -mt-0.5" />
          Grand Total:{" "}
          <span className="mono font-semibold text-zinc-800">
            ₹{" "}
            {totals.grand.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={submit}
          disabled={saving || !canSubmit}
          data-testid="manual-create-btn-bottom"
        >
          <Save size={14} className="mr-1" />
          {saving ? "Creating…" : "Create & Preview Invoice"}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value, bold, testid }) {
  return (
    <div className={`flex justify-between px-3 py-1.5 ${bold ? "bg-zinc-50 font-bold" : ""}`}>
      <span className="font-sans text-zinc-600">{label}</span>
      <span data-testid={testid}>
        ₹{" "}
        {Number(value || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
    </div>
  );
}
